import { shallow } from 'zustand/shallow';
import { devtools } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';
import type { StoreApi } from 'zustand';

import createSearchQuery, { type SearchQuerySlice } from './searchQuery';
import createUserSettings, { type UserSettingsSlice } from './userSettings';
import createSearchRegistry, { type SearchRegistrySlice } from './searchRegistry';
import createDashboardDraft, { type DashboardDraftSlice } from './customDashboardDraft';
import createDashboardConfiguration, { type DashboardConfigurationSlice } from './dashboardConfiguration';

export type StoreState = {
  searchQuery: SearchQuerySlice;
  userSettings: UserSettingsSlice;
  searchRegistry: SearchRegistrySlice;
  dashboardDraft: DashboardDraftSlice;
  dashboardConfiguration: DashboardConfigurationSlice;
};

export type StoreSet = StoreApi<StoreState>['setState'];
export type StoreGet = StoreApi<StoreState>['getState'];

// use when custom comparisson fn is required
export const useStoreBase = createWithEqualityFn<StoreState>()(
  devtools(
    (set, get) => ({
      searchQuery: { ...createSearchQuery(set, get) },
      userSettings: { ...createUserSettings(set) },
      searchRegistry: { ...createSearchRegistry(set, get) },
      dashboardDraft: { ...createDashboardDraft(set, get) },
      dashboardConfiguration: { ...createDashboardConfiguration(set) },
    }),
    // Redux DevTools extension integration, DEV-only — replaces simple-zustand-devtools
    { name: 'Store', enabled: import.meta.env.DEV },
  ),
);

// use shallow as default comparisson fn
export const useStore = <T>(selector: (state: StoreState) => T) => useStoreBase(selector, shallow);

export default useStore;
